import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Button } from "devextreme-react/button";
import {
  DateBox,
  Popup,
  RadioGroup,
  SelectBox,
  TextBox,
} from "devextreme-react";

import MonitorCard, {
  ImageMagnifier,
} from "@common-components/cards/MonitorCard";
import MonitorActions from "@common-components/date-filter/MonitorActions";
import FilletInfo from "@common-components/filet-information/FilletInfo";
import FilletInformationList from "@common-components/filet-information/FilletInformationList";
import MonitorDropdownFilters from "@common-components/filter-dropdown/MonitorDropdownFilters";

import { useFetch } from "@hooks/useBatchIntakeApi";
import { useFetch as useMonitorFetch } from "@hooks/useMonitorApi";
import { useFetch as useStatFetch } from "@hooks/useStatApi";
import MONITOR_API from "api/monitorApi";
import INTAKE_API from "api/intakeApi";
import { apiGet } from "api/api-monitor";
// import { apiGet as getStatisticsbyBatch } from "api/api-stats";

import { HubConnectionBuilder } from "@microsoft/signalr";
import BatchInfoCard from "@common-components/cards/BatchInfoCard";
import { toLocalISOString } from "utils/helpers";
import Loader from "@common-components/loader/Loader";

const Monitor = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });

  const [filletInformation, setFilletInformation] = useState(null);
  const [pid, setPid] = useState("");
  const [batcheInfo, setBatchInfo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [monitorView, setMonitorView] = useState(1);
  const [supplier, setSupplier] = useState(localStorage.getItem("supplierId"));
  const [batches, setBatches] = useState([]);
  const [processedImagesData, setProcessImagesData] = useState([]);
  const [colorGradingInfo, setColorGradingInfo] = useState(null);
  const [pageNo, setPageNo] = useState(0);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [liveImages, setLiveImages] = useState(
    JSON.parse(localStorage.getItem("recentIntake")) || []
  );
  const [pSize, setPSize] = useState(null);
  const hSwiper = useRef(null);
  const lSwiper = useRef(null);
  const [direction, setDirection] = useState("ltr");
  const [slideLength, setSlideLength] = useState(0);
  const [newImageAdded, setNewImageAdded] = useState(false);
  const [bool, setBool] = useState(false);
  const [selectedPageIdx, setSelectedPageIdx] = useState(null);
  const [selectedPageNo, setSelectedPageNo] = useState(null);
  const [previousView, setPreviousView] = useState({ prev: null, page: null });
  const [averageData, setAverageData] = useState();
  const [batchData, setBatchData] = useState(null);
  const [supplierName, setSupplierName] = useState(null);
  const [isPidExist, setIsPidExist] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDateRangeExist, setIsDateRangeExist] = useState(false);
  const [visible, setVisible] = useState(false);
  const [advanceFilterVisible, setAdvanceFilterVisible] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [loader, setLoader] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isActual, setIsActual] = useState(false);
  const [supplierForAdvanceSearch, setSupplierForAdvanceSearch] = useState(0);
  const [triggering, setTriggering] = useState(false);

  const [batchDataInfo, setBatchDataInfo] = useState(null);
  const dateRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);

    return {
      formattedStartDate: start.toISOString().split("T")[0],
      formattedEndDate: end.toISOString().split("T")[0],
    };
  }, []);

  const statParams = useMemo(
    () => ({
      supplierId: supplier,
      startDate: dateRange.formattedStartDate,
      endDate: dateRange.formattedEndDate,
    }),
    [supplier, dateRange]
  );

  const getRowPerView = (monitorView) => {
    if (isMobile) return 1;
    if (isTablet) return monitorView === 1 ? 2 : 1;
    if (isDesktop) return monitorView === 1 ? 4 : 1;
  };

  const getGridRowPerView = (monitorView) => {
    if (isMobile) return 1;
    if (isTablet) return monitorView === 1 ? 2 : 1;
    if (isDesktop) return monitorView === 1 ? 3 : 1;
  };

  const { data: suppliers } = useFetch("suppliers", INTAKE_API.getSuppliers, {
    PageNo: 0,
    PageSize: 1000,
  });

  const supplierIdForMonitorAverage =
    !supplier || supplier === "undefined" ? 0 : supplier;

  const { data: getMonitorAverage } = useMonitorFetch(
    "getMonitorAverage",
    MONITOR_API.getMonitorAverage,
    {
      supplierId: supplierIdForMonitorAverage,
    },
    {
      enabled: true,
    }
  );

  const getFilletInformation = async (id, type) => {
    const response = await apiGet(MONITOR_API.getFilletInfo, {
      processedProductID: id,
    });

    // console.log(response.data,'filletInformation')
    setFilletInformation({
      filletDetails: response.data[0],
    });

    if (type === "hswiper") {
      const imageFound = await processedImagesData.find(
        (data) => data.processedProductID === id
      );
      if (!imageFound) {
        getProcessedImage(pageNo - 1);
        setPageNo(pageNo - 1);
        hSwiper.current?.swiper?.autoplay?.stop();
      }
    }
  };

  const getBatch = async (batch) => {
    setBatchInfo(batch);
    setPageNo(0); // Reset page when batch changes
    // getStatsByBatch(batch);
  };

  const { data: colorGradingInfoResponse } = useStatFetch(
    MONITOR_API.getStatisticsbyBatch,
    MONITOR_API.getStatisticsbyBatch,
    statParams,
    {
      enabled: !supplier || supplier === "undefined" ? false : true, // Only fetch if supplier is truthy (non-null)
    }
  );

  const forwardTenPages = () => {
    const next = pageNo + 10;

    if (next < totalPageCount) {
      setDirection("ltr");
      setPageNo(next);
      getProcessedImage(next);
    } else if (pageNo < totalPageCount - 1) {
      // If not enough for 10 pages ahead, go to the last page
      setDirection("ltr");
      const lastPage = totalPageCount - 1;
      setPageNo(lastPage);
      getProcessedImage(lastPage);
    }
  };

  const backwardTenPages = () => {
    const prev = pageNo - 10;

    if (prev >= 0) {
      setDirection("rtl");
      setPageNo(prev);
      getProcessedImage(prev);
    } else if (pageNo > 0) {
      // If going back 10 would go negative, go to page 0
      setDirection("rtl");
      setPageNo(0);
      getProcessedImage(0);
    }
  };

  useEffect(() => {
    if (isPidExist) {
      setIsPidExist(true);
    }
  }, [processedImagesData && isPidExist]);

  const getProcessedImage = async (page = pageNo) => {
    if (isPidExist && selectedFilter === "pid") {
      setIsLoading(true);
      // console.log(isPidExist)
      pauseHero();
      const response = await apiGet(MONITOR_API.getFilletInfoByPID, {
        PID: pid,
      });
      setProcessImagesData(response.data);
      setIsLoading(false);

      // console.log(isPidExist, "selectedFilter11", selectedFilter);
      // setSelectedFilter("");
    } else if (
      isDateRangeExist &&
      selectedFilter === "date" &&
      startDate &&
      endDate
    ) {
      setLoader(true);
      setIsLoading(true);
      const pageSize =
        getRowPerView(monitorView) * getGridRowPerView(monitorView);
      setPSize(pageSize);
      // d.toLocaleTimeString

      if (page == -1) {
        setPageNo(0);
      }
      // console.log(toLocalISOString(startDate), endDate, "startDate, endDate");
      const response = await apiGet(MONITOR_API.getFilletInfoByDate, {
        startDate: toLocalISOString(startDate),
        endDate: toLocalISOString(endDate),
        supplierId: supplierForAdvanceSearch,
        // batchNumber: batcheInfo,
        PageNo: page == -1 ? 0 : page,
        PageSize: pageSize,
      });

      console.log(response, "startDate, endDate");
      setTotalPageCount(response.data.totalPageCount); // Save total page count
      setSlideLength(response.data.data.length);
      // if (pageSize === 1) {
      //   setProcessImagesData(response.data.data);
      // }
      // {
      setProcessImagesData((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        const newDataArray = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        if (prevArray.length === 0 && newDataArray.length === 0)
          return prevArray;

        if (prevArray.length === 0) return newDataArray;
        if (newDataArray.length === 0) return prevArray;

        // Otherwise, merge both
        return [...prevArray, ...newDataArray];
      });
      // }
      // setProcessImagesData(response.data);
      setLoader(false);
      setIsLoading(false);
    } else if (
      isActual &&
      selectedFilter === "actual" &&
      startDate &&
      endDate
    ) {
      setLoader(true);
      setIsLoading(true);
      const pageSize =
        getRowPerView(monitorView) * getGridRowPerView(monitorView);
      setPSize(pageSize);
      // d.toLocaleTimeString

      if (page == -1) {
        setPageNo(0);
      }
      // console.log(toLocalISOString(startDate), endDate, "startDate, endDate");
      const response = await apiGet(MONITOR_API.getFilletInfoByDate, {
        startDate: toLocalISOString(startDate),
        endDate: toLocalISOString(endDate),

        // batchNumber: batcheInfo,
        PageNo: page == -1 ? 0 : page,
        PageSize: pageSize,
      });

      console.log(response, "startDate, endDate");
      setTotalPageCount(response.data.totalPageCount); // Save total page count
      setSlideLength(response.data.data.length);
      // if (pageSize === 1) {
      //   setProcessImagesData(response.data.data);
      // }
      // {
      setProcessImagesData((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        const newDataArray = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        if (prevArray.length === 0 && newDataArray.length === 0)
          return prevArray;

        if (prevArray.length === 0) return newDataArray;
        if (newDataArray.length === 0) return prevArray;

        // Otherwise, merge both
        return [...prevArray, ...newDataArray];
      });
      // }
      // setProcessImagesData(response.data);
      setLoader(false);
      setIsLoading(false);
    } else {
      if (batcheInfo) {
        setIsLoading(true);
        // debugger;
        const pageSize =
          getRowPerView(monitorView) * getGridRowPerView(monitorView);
        setPSize(pageSize);
        // const pageSize =
        //   getRowPerView(monitorView) * getGridRowPerView(monitorView);

        if (page == -1) {
          setPageNo(0);
        }
        const response = await apiGet(MONITOR_API.getMonitorImages, {
          BatchNo: batcheInfo ? batcheInfo : "",
          PageNo: page == -1 ? 0 : page,
          PageSize: pageSize,
        });

        setTotalPageCount(response.data.totalPageCount); // Save total page count
        setSlideLength(response.data.data.length);
        // if (pageSize === 1) {
        //   setProcessImagesData(response.data.data);
        // }
        // if (pageSize !== 1) {
        setProcessImagesData((prev) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          const newDataArray = Array.isArray(response.data.data)
            ? response.data.data
            : [];

          if (prevArray.length === 0 && newDataArray.length === 0)
            return prevArray;

          if (prevArray.length === 0) return newDataArray;
          if (newDataArray.length === 0) return prevArray;

          // Otherwise, merge both
          return [...prevArray, ...newDataArray];
        });

        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (processedImagesData.length > pSize) {
      hSwiper.current.swiper?.slideNext();
      // setTimeout(() => {
      // const tempArr = processedImagesData.slice(pSize);
      // tempArr.slice(pSize);
      const tempArr = processedImagesData;
      tempArr.splice(0, slideLength);
      setProcessImagesData(tempArr);
      // }, 6000);
    }
  }, [processedImagesData]);

  useEffect(() => {
    let interval = null;

    if (isPlaying) {
      interval = setInterval(() => {
        const next = pageNo + 1;

        if (next < totalPageCount) {
          setDirection("ltr");
          setPageNo(next);
          getProcessedImage(next);
        } else {
          // Optionally restart from page 0 to loop
          // setPageNo(0);
          // getProcessedImage(0);
          pauseHero(); // or pause if you want it to stop
        }
      }, 6000); // 6-second interval
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, pageNo, totalPageCount]);

  useEffect(() => {
    if (batcheInfo) {
      getProcessedImage(0);
    } else {
      setTotalPageCount(0);
    }
  }, [batcheInfo]);

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(
        `${process.env.REACT_APP_API_MON_WEBHOOK_URL}/newprocessedimagenotification`
      )
      .withAutomaticReconnect()
      .build();

    const handler = async (data) => {
      console.log("New image record:", data.processedProductID);

      try {
        const response = await apiGet(MONITOR_API.getLiveMonitorImages, {
          PID: data.processedProductID,
        });

        // console.log(response)
        if (response.data.length > 0) {
          const newItem = response.data[0];

          const image = newItem.processedProductImage.find((data) =>
            data.imageType.includes("Single")
          );

          const items = JSON.parse(localStorage.getItem("recentIntake"));

          const hasDifferentID = items?.some(
            (item) => item.processedProductID === data.processedProductID
          );

          if (image) {
            if (!hasDifferentID | (items === null)) {
              setLiveImages((prev) => {
                // Create a Set of existing IDs for fast lookup
                const idSet = new Set(
                  prev.map((img) => img.processedProductID)
                );
                if (idSet.has(newItem.processedProductID)) {
                  return prev; // Already present, do nothing
                }
                const updated = [...prev, newItem];
                if (updated.length > 5) updated.shift();
                // Update localStorage with the new array
                localStorage.setItem("recentIntake", JSON.stringify(updated));
                return updated;
              });

              setNewImageAdded(true); // Trigger effect for animation
              setBool(!bool);
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    connection.on("SendNewProcessedImageNotification", handler);

    connection
      .start()
      .catch((err) => console.error("SignalR Connection Error:", err));

    return () => {
      connection.off("SendNewProcessedImageNotification", handler);
      connection.stop();
    };
  }, []);

  useEffect(() => {
    if (newImageAdded && liveImages.length > 1) {
      const swiperInstance = lSwiper.current?.swiper;

      if (!swiperInstance) return;

      // Force swiper to update its slide state
      swiperInstance.update();

      // If it's at the end, go back one and slide to new end
      if (swiperInstance.isEnd) {
        swiperInstance.slideTo(swiperInstance.activeIndex - 1, 0); // go back without animation
        swiperInstance.slideTo(swiperInstance.activeIndex + 1); // move to new last slide
      } else {
        swiperInstance.slideNext();
      }

      setNewImageAdded(false); // Reset flag

      // After 6 seconds, remove the oldest and reset slider to show full loop
      if (liveImages.length > 4) {
        setTimeout(() => {
          const tempArr = [...liveImages];
          tempArr.shift();
          setLiveImages(tempArr);

          // Reset Swiper to 0 to create infinite loop illusion
          swiperInstance.slideTo(1, 0); // quick jump forward
          swiperInstance.slideTo(0, 0); // back to start smoothly
        }, 100);
      }
    }
  }, [liveImages, newImageAdded]);

  useEffect(() => {
    if (supplier) {
      getBatches();
      setIsActual(false);
      setIsPidExist(false);
      setIsDateRangeExist(false);
      setSelectedFilter("");
      setPid(null);
      setStartDate(null);
      setEndDate(null);
      setTotalPageCount(0);
    }
  }, [supplier]);

  useEffect(() => {
    setProcessImagesData([]);

    // if (batcheInfo) {
    let globalIndex = 0;

    if (monitorView !== 1) {
      if (previousView.prev == 2 || previousView == 3) {
        globalIndex = previousView.page;
      } else {
        globalIndex = selectedPageIdx ? pageNo * pSize + selectedPageIdx : 0;
        setPreviousView({ prev: monitorView, page: globalIndex });
      }
    } else {
      if (previousView.page !== null) {
        globalIndex = Math.ceil(previousView.page / 12) - 1;
      } else {
        globalIndex = 0;
      }
    }

    setPageNo(globalIndex);
    getProcessedImage(globalIndex);
    // }
  }, [monitorView, isPidExist, isDateRangeExist, isActual, triggering]);

  const getBatches = async () => {
    if (!supplier || supplier === "undefined") {
      return;
    }
    const batches = await apiGet(MONITOR_API.getMonitorBatches, {
      supplierID: supplier,
    });
    setBatches(batches.data);
    // if (batches.data[0]?.batchNumber) {
    //   setBatchInfo(batches.data[0].batchNumber);
    // } else {
    //   setBatchInfo(null);
    // }

    // batches.data.map(data=>data.batchNumber)

    const filterBatch = batches?.data.find(
      (data) => data?.batchNumber === localStorage.getItem("batchNum")
    );

    if (filterBatch) {
      setBatchInfo(filterBatch?.batchNumber);
    } else {
      if (batches.data[0]?.batchNumber) {
        setProcessImagesData([]);
        setBatchInfo(batches.data[0].batchNumber);
      } else {
        setBatchInfo(null);
      }
    }
  };

  const playHero = () => {
    hSwiper.current?.swiper?.autoplay?.start();
    setIsPlaying(true);
  };

  const pauseHero = () => {
    hSwiper.current?.swiper?.autoplay?.stop();
    setIsPlaying(false);
  };

  const nextPage = () => {
    setDirection("ltr");
    const next = pageNo + 1;
    if (next < totalPageCount) {
      setPageNo(next);
      getProcessedImage(next);
    }
  };

  const prevPage = () => {
    setDirection("rtl");
    const prev = Math.max(0, pageNo - 1);
    setPageNo(prev);
    getProcessedImage(prev);
  };

  const handleValueChange = (e) => {
    const newValue = e?.value;
    setPid(newValue);
    if (newValue === "" || newValue?.length > 0) {
      // issue
      setIsPidExist(false);
    }
  };

  return (
    <>
      <Popup
        visible={advanceFilterVisible}
        onHiding={() => setAdvanceFilterVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={`Advanced Search`}
        width={"45%"}
        height={"45%"}

        // fullScreen={true} // 👈 Fullscreen mode enabled
      >
        <div className=" h-full flex justify-between flex-col">
          <div className="flex gap-3">
            <RadioGroup
              // disabled={isEdit}
              items={[
                { id: "pid", text: "" },
                { id: "date", text: "" },
              ]}
              valueExpr="id"
              displayExpr="text"
              layout="vertical"
              value={selectedFilter}
              onValueChanged={(e) => setSelectedFilter(e.value)}
              itemRender={(item) => (
                <div style={{ marginBottom: "40px" }}>{item.text}</div>
              )}
            />
            <div>
              <div className="flex flex-col items-start">
                <p className="mb-1 text-[14px] font-medium">PID</p>
                <TextBox
                  value={pid}
                  onValueChanged={handleValueChange}
                  placeholder="Enter PID"
                  showClearButton={true}
                  label="My Label"
                  labelMode="hidden"
                  // className="w-full"
                  // onEnterKey={() => {
                  //   if (pid.length > 0) {
                  //     setIsPidExist(true);
                  //     // setAdvanceFilterVisible(false);
                  //     setSupplier(null);
                  //     setBatchInfo(null);
                  //   }
                  // }}
                />
              </div>
              <div style={{ display: "flex", gap: "1rem", marginTop: 10 }}>
                <div className="flex flex-col items-start">
                  <p className="mb-1 text-[14px] font-medium">From Date</p>
                  <DateBox
                    type="datetime"
                    value={startDate}
                    onValueChanged={(e) => {
                      setStartDate(e.value);
                      console.log(e.value);
                    }}
                    // label="Start Date & Time"
                    labelMode="hidden"
                    placeholder="dd/mm/yyyy --:-- --Start"
                    displayFormat="yyyy-MM-dd HH:mm" // 👈 24-hour format
                    showClearButton={true}
                  />
                </div>
                <div className="flex flex-col items-start">
                  <p className="mb-1 text-[14px] font-medium">To Date</p>
                  <DateBox
                    type="datetime"
                    value={endDate}
                    onValueChanged={(e) => {
                      setEndDate(e.value);
                    }}
                    label="End Date & Time"
                    labelMode="hidden"
                    disabled={!startDate}
                    placeholder="dd/mm/yyyy --:-- --"
                    showClearButton={true}
                    displayFormat="yyyy-MM-dd HH:mm" // 👈 24-hour format
                    min={startDate} // Prevent selecting an end before the start
                  />
                </div>
                {suppliers && suppliers?.data.length > 0 && (
                  <div className="flex flex-col items-start">
                    <p className="mb-1 text-[14px] font-medium">Supplier</p>
                    <SelectBox
                      dataSource={[
                        { supplierID: 0, name: "All" },
                        ...suppliers?.data,
                      ]}
                      displayExpr="name" // Display text
                      valueExpr="supplierID" // Value field
                      value={parseInt(supplierForAdvanceSearch)}
                      searchEnabled={true}
                      onValueChanged={(e) => {
                        // setSupplier(e.value);
                        setSupplierForAdvanceSearch(e.value);
                      }}
                      placeholder="Select Supplier"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex w-full justify-end">
            <Button
              text="Contained"
              type="default"
              onClick={() => {
                if (selectedFilter === "pid") {
                  if (pid?.length > 0) {
                    setIsPidExist(true);
                  }
                  // setAdvanceFilterVisible(false);
                  setSupplier(null);
                  setBatchInfo(null);
                  setAdvanceFilterVisible(false);
                  setStartDate(null);
                  setEndDate(null);
                  setSupplierForAdvanceSearch(0);
                }
                if (selectedFilter === "date") {
                  if (startDate && endDate) {
                    setIsDateRangeExist(true);
                  }
                  setSupplier(null);
                  setBatchInfo(null);
                  setPid(null);
                  setIsPidExist(false);
                  setAdvanceFilterVisible(false);
                }
                setTriggering(!triggering); // Trigger the effect to fetch data
              }}
            >
              Search
            </Button>
          </div>
        </div>
      </Popup>

      <Popup
        visible={visible}
        onHiding={() => setVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={`PID: ${selectedMonitor?.processedProductID}`}
        fullScreen={true} // 👈 Fullscreen mode enabled
      >
        <div className="grid grid-cols-12 gap-2">
          <div
            className={`w-[100%] col-span-12 lg:col-span-8 min-h-24 flex flex-col justify-between border-gray-400 rounded-md bg-gray-50 dark-bg overflow-hidden`}
          >
            {/* Header */}
            <div
              className={`w-full p-3 flex items-center justify-between border-b border-gray-200 dark-bg ${monitorView === 1 ? "flex-col" : "flex-row"}`}
            >
              <p className="mb-0">PID: {selectedMonitor?.processedProductID}</p>
              <p className="mb-0">
                {selectedMonitor?.validFromUTC?.split("T")[0]}{" "}
                {selectedMonitor?.validFromUTC?.split("T")[1].split(":")[0]}:
                {selectedMonitor?.validFromUTC?.split("T")[1].split(":")[1]} UTC
              </p>
            </div>

            {/* Image with Zoom Effect */}

            {monitorView === 1 &&
              selectedMonitor?.processedProductImage.map((data) => {
                return (
                  data.imageType === "Single" && (
                    <ImageMagnifier
                      src={`${data.url}`}
                      key={data.url}
                      className={`w-100`}
                      magnifierHeight={400}
                      magnifierWidth={400}
                      zoomLevel={2}
                      alt={`${data.url}`}
                      isPlaying={isPlaying}
                    />
                  )
                );
              })}

            {monitorView === 2 && (
              <div className="flex flex-col-reverse justify-center items-center gap-1">
                {selectedMonitor?.processedProductImage?.find((data) =>
                  data?.imageType?.includes("Dual")
                )
                  ? selectedMonitor?.processedProductImage?.map((data) => {
                      return (
                        <>
                          {data.imageType === "Single" && (
                            <ImageMagnifier
                              src={`${data.url}`}
                              key={data.url}
                              className={`w-100`}
                              magnifierHeight={400}
                              magnifierWidth={400}
                              zoomLevel={2}
                              alt={`${data.url}`}
                              isPlaying={isPlaying}
                            />
                          )}
                          {data.imageType === "Dual" && (
                            <ImageMagnifier
                              src={`${data.url}`}
                              key={data.url}
                              className={`w-100`}
                              magnifierHeight={400}
                              magnifierWidth={400}
                              zoomLevel={2}
                              alt={`${data.url}`}
                              isPlaying={isPlaying}
                            />
                          )}
                        </>
                      );
                    })
                  : monitorView === 2 && (
                      <div className="mt-[-100rem] flex justify-center items-center">
                        <p>Dual view not available</p>
                      </div>
                    )}
              </div>
            )}
            {monitorView === 3 && (
              <div className="flex flex-col-reverse justify-center items-center gap-1 bg-red-400">
                {selectedMonitor?.processedProductImage?.find((data) =>
                  data?.imageType?.includes("Triple")
                ) &&
                selectedMonitor?.processedProductImage?.find((data) =>
                  data?.imageType?.includes("Dual")
                )
                  ? selectedMonitor?.processedProductImage.map((data) => {
                      return (
                        <>
                          {data.imageType === "Single" && (
                            <ImageMagnifier
                              src={`${data.url}`}
                              key={data.url}
                              className={`w-100`}
                              magnifierHeight={400}
                              magnifierWidth={400}
                              zoomLevel={2}
                              alt={`${data.url}`}
                              isPlaying={isPlaying}
                            />
                          )}
                          {data.imageType === "Dual" && (
                            <ImageMagnifier
                              src={`${data.url}`}
                              key={data.url}
                              className={`w-100`}
                              magnifierHeight={400}
                              magnifierWidth={400}
                              zoomLevel={2}
                              alt={`${data.url}`}
                              isPlaying={isPlaying}
                            />
                          )}
                          {data.imageType === "Triple" && (
                            <ImageMagnifier
                              src={`${data.url}`}
                              key={data.url}
                              className={`w-100`}
                              magnifierHeight={400}
                              magnifierWidth={400}
                              zoomLevel={2}
                              alt={`${data.url}`}
                              isPlaying={isPlaying}
                            />
                          )}
                        </>
                      );
                    })
                  : monitorView === 3 && (
                      <div className="flex mt-[-100rem] justify-center items-center">
                        <p>Triple view not available</p>
                      </div>
                    )}
              </div>
            )}
          </div>
          <div className="col-span-12 lg:col-span-4">
            <FilletInformationList
              filletInformation={filletInformation}
              getMonitorAverage={getMonitorAverage?.data}
              colorGradingInfo={null}
            />
          </div>
        </div>
      </Popup>
      <div className="dx-card responsive-paddings border-0">
        <div className="grid lg:grid-cols-3 grid-cols-1 gap-2">
          <div className="lg:col-span-2 flex-wrap items-center justify-center gap-2">
            <div className="flex items-center justify-between">
              <div className="w-full flex items-center">
                <p className="whitespace-nowrap text-xl font-medium mb-0">
                  Recent Intake:
                </p>
                <div className="flex-grow h-px bg-gray-300 light-bg ml-2"></div>
              </div>
              {/* <a href="/quality/fillet/selectedMonitor" target="_blank"> */}
              <Button
                onClick={() => {
                  setVisible(true);
                }}
                className="ms-2"
                disabled={!selectedMonitor}
                icon="fullscreen"
              />
              {/* </a> */}
            </div>

            <MonitorCard
              key={`recent-intake-${liveImages?.length}`}
              sliderType="rSlider"
              navigation={false}
              viewMode={1}
              rowPerView={4}
              swiperRef={lSwiper}
              playFilletSlider={true}
              gridRows={1}
              filletInfo={getFilletInformation}
              data={liveImages}
              isPlaying={true}
              filletInformation={filletInformation}
              direction={"rtl"}
              setSelectedPageIdx={setSelectedPageIdx}
              setSelectedPageNo={setSelectedPageNo}
              setPreviousView={setPreviousView}
              selectedMonitor={selectedMonitor}
              setSelectedMonitor={setSelectedMonitor}
              nextPage={() => {}}
              prevPage={() => {}}
              isPidExist={false}
              isLoading={false}
            />
            <div className="block lg:grid lg:grid-cols-2 space-y-3 lg:space-y-0">
              <div>
                <MonitorDropdownFilters
                  setMonitorView={setMonitorView}
                  batches={batches}
                  batch={batcheInfo}
                  suppliers={suppliers?.data}
                  getBatch={getBatch}
                  setSupplier={setSupplier}
                  supplier={supplier}
                  setBatchData={setBatchData}
                  setSupplierName={setSupplierName}
                  value={pid}
                  setValue={setPid}
                  isPidExist={isPidExist}
                  setIsPidExist={setIsPidExist}
                  setIsDateRangeExist={setIsDateRangeExist}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  setBatchInfo={setBatchInfo}
                  setSelectedFilter={setSelectedFilter}
                  setPid={setPid}
                  isDateRangeExist={isDateRangeExist}
                  loader={loader}
                  setProcessImagesData={setProcessImagesData}
                  selectedFilter={selectedFilter}
                  setIsActual={setIsActual}
                  isActual={isActual}
                />
              </div>
              <div className="flex justify-between md:flex-row flex-col  items-center gap-2">
                {selectedFilter !== "pid" && (
                  <MonitorActions
                    Prev={prevPage}
                    Next={nextPage}
                    isActiveNextButton={
                      pageNo + 1 < totalPageCount ? false : true
                    }
                    isActivePrevButton={pageNo === 0}
                    isPlaying={isPlaying}
                    play={playHero}
                    pause={pauseHero}
                    forwardTenPages={forwardTenPages}
                    backwardTenPages={backwardTenPages}
                  />
                )}
                <SelectBox
                  dataSource={[
                    { id: 1, name: "Single View" },
                    { id: 2, name: "Dual View" },
                    { id: 3, name: "Triple View" },
                  ]}
                  displayExpr="name"
                  valueExpr="id"
                  value={monitorView}
                  className="custom-selectbox w-full"
                  inputAttr={{ class: "text-blue-500" }}
                  dropDownButton={{ class: "bg-blue-500 text-white" }}
                  dropDownOptions={{ class: "bg-gray-100" }}
                  onValueChanged={(e) => {
                    setMonitorView(e.value);
                  }}
                />
              </div>
              <a
                className="pt-2 cursor-pointer w-[120px]"
                onClick={() => {
                  setAdvanceFilterVisible(true);
                  setIsActual(false);
                }}
              >
                Advanced Search
              </a>
            </div>
            {!isPidExist && (
              <div className="text-center mt-2 flex-none lg:block">{`${pageNo + 1}/${totalPageCount}`}</div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center mt-10">
                <Loader />
              </div>
            ) : processedImagesData?.length > 0 ? (
              <MonitorCard
                key={`historical-${monitorView}-${pageNo}-${processedImagesData?.length}`}
                sliderType="hSlider"
                navigation={true}
                viewMode={monitorView}
                rowPerView={getRowPerView(monitorView)}
                swiperRef={hSwiper}
                playFilletSlider={isPlaying}
                gridRows={getGridRowPerView(monitorView)}
                filletInfo={getFilletInformation}
                data={processedImagesData}
                isPlaying={isPlaying}
                filletInformation={filletInformation}
                direction={direction}
                setSelectedPageIdx={setSelectedPageIdx}
                setSelectedPageNo={setSelectedPageNo}
                setIsPlaying={setIsPlaying}
                setPreviousView={setPreviousView}
                selectedMonitor={selectedMonitor}
                setSelectedMonitor={setSelectedMonitor}
                nextPage={nextPage}
                prevPage={prevPage}
                isPidExist={isPidExist}
                isLoading={isLoading}
              />
            ) : (
              <div className="mt-2 text-center no-data-text">No Data Found</div>
            )}
          </div>

          <div>
            <BatchInfoCard
              type="monitor"
              setBatchData={setBatchDataInfo}
              batchDatas={batchData}
              supplierName={supplierName}
            />
            <FilletInformationList
              filletInformation={filletInformation}
              getMonitorAverage={getMonitorAverage?.data}
              colorGradingInfo={colorGradingInfoResponse?.data}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Monitor;






